import { useCallback, useEffect, useRef, useState } from 'react';
import type { Eu, Falha } from '../../acesso';
import { podeFazer } from '../../acesso';
import type { Sessao } from '../../auth';
import { EstadoVazio, ErroDeArea } from '../../design/Estados';
import type { Escrita } from '../../giro';
import { TopoMovel } from '../../shell/ShellBalconista';
import { lerMeuMural, registrarLeitura } from '../../administracao';
import type { AvisoDoMural } from '../../administracao';
import { CartaoDeAviso, Desfecho } from './comunicacao-comum';
import './mural.css';

/**
 * TELA 30 B — OS AVISOS DE QUEM ESTÁ LOGADO (balconista e admin).
 *
 * Três fatos, três registros no servidor: ao APARECER na tela conta como visto; ao ABRIR, lido;
 * "Li e entendi" só existe quando o aviso pede. Urgente e fixado vêm primeiro (ordem do servidor).
 * Nada aqui bloqueia o trabalho: é uma lista, não um modal.
 */

type Carga =
  | { readonly fase: 'carregando' }
  | { readonly fase: 'erro'; readonly falha: Falha }
  | { readonly fase: 'pronto'; readonly avisos: readonly AvisoDoMural[] };

export function MeuMural({ sessao, eu }: { sessao: Sessao; eu: Eu | null }) {
  const [carga, setCarga] = useState<Carga>({ fase: 'carregando' });
  const [abertos, setAbertos] = useState<ReadonlySet<string>>(new Set());
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [falhas, setFalhas] = useState<Readonly<Record<string, Escrita<null>>>>({});
  const vistos = useRef(new Set<string>());
  const balconista =
    eu !== null && podeFazer(eu, 'vendas.registrar') && !podeFazer(eu, 'vendas.ver_todas');

  const carregar = useCallback(async () => {
    const r = await lerMeuMural();
    setCarga(r.ok ? { fase: 'pronto', avisos: r.dados.avisos } : { fase: 'erro', falha: r.falha });
  }, []);
  useEffect(() => {
    void carregar();
  }, [carregar]);

  const marcar = useCallback(
    (id: string, campo: 'vistoEm' | 'lidoEm' | 'confirmadoEm') =>
      setCarga((c) =>
        c.fase === 'pronto'
          ? {
              ...c,
              avisos: c.avisos.map((a) =>
                a.id === id
                  ? {
                      ...a,
                      [campo]: a[campo] ?? new Date().toISOString(),
                      ...(campo !== 'vistoEm'
                        ? { lidoEm: a.lidoEm ?? new Date().toISOString() }
                        : {}),
                    }
                  : a,
              ),
            }
          : c,
      ),
    [],
  );

  // Apareceu na tela = visto. Uma vez por aviso; falha de rede não vira erro na tela.
  useEffect(() => {
    if (carga.fase !== 'pronto') return;
    for (const a of carga.avisos) {
      if (a.vistoEm || vistos.current.has(a.id)) continue;
      vistos.current.add(a.id);
      void registrarLeitura(sessao, a.id, 'visto').then((r) => {
        if (r.tipo === 'confirmada') marcar(a.id, 'vistoEm');
      });
    }
  }, [carga, sessao, marcar]);

  const abrir = (a: AvisoDoMural) => {
    const novo = new Set(abertos);
    if (novo.has(a.id)) novo.delete(a.id);
    else {
      novo.add(a.id);
      if (!a.lidoEm)
        void registrarLeitura(sessao, a.id, 'lido').then((r) => {
          if (r.tipo === 'confirmada') marcar(a.id, 'lidoEm');
        });
    }
    setAbertos(novo);
  };

  const confirmar = async (a: AvisoDoMural) => {
    setConfirmando(a.id);
    const r = await registrarLeitura(sessao, a.id, 'confirmado');
    setConfirmando(null);
    if (r.tipo === 'confirmada') {
      marcar(a.id, 'confirmadoEm');
      setFalhas(({ [a.id]: _, ...resto }) => resto);
    } else setFalhas((f) => ({ ...f, [a.id]: r }));
  };

  const avisos = carga.fase === 'pronto' ? carga.avisos : [];

  return (
    <>
      {balconista && <TopoMovel eu={eu} voltar="#/inicio" titulo="Avisos" />}
      <div className={`mu-meu${balconista ? ' mu-meu-balconista' : ''}`}>
        <div className="mu-meu-cabeca">
          <h1 className="mu-meu-titulo">Avisos</h1>
          <div className="mu-meu-sub">
            Comunicados da Rede Inova para você · urgente e fixado primeiro
          </div>
        </div>
        {carga.fase === 'carregando' && (
          <div className="mu-meu-lista" aria-busy="true">
            <div className="mu-cartao-esqueleto" />
            <div className="mu-cartao-esqueleto" />
          </div>
        )}
        {carga.fase === 'erro' && <ErroDeArea oQue="os avisos" aoTentar={() => void carregar()} />}
        {carga.fase === 'pronto' && avisos.length === 0 && (
          <EstadoVazio
            icone="bell"
            titulo="Nenhum aviso agora"
            texto="Quando a gerência publicar um comunicado para você, ele aparece aqui."
          />
        )}
        {avisos.length > 0 && (
          <div className="mu-meu-lista">
            {avisos.map((a) => {
              const aberto = abertos.has(a.id);
              const pede = a.exigeConfirmacao && !a.confirmadoEm;
              return (
                <CartaoDeAviso
                  key={a.id}
                  aviso={a}
                  resumido={!aberto}
                  aoAbrir={() => abrir(a)}
                  confirmar={
                    pede ? (
                      <>
                        <button
                          type="button"
                          className="mu-confirmar"
                          disabled={confirmando === a.id}
                          onClick={() => void confirmar(a)}
                        >
                          {confirmando === a.id ? 'Confirmando…' : 'Li e entendi'}
                        </button>
                        <Desfecho
                          escrita={falhas[a.id] ?? null}
                          incerta={
                            'Não sabemos se chegou. Toque de novo em "Li e entendi" — confirmar duas vezes não duplica.'
                          }
                        />
                      </>
                    ) : null
                  }
                  rodape={
                    <button type="button" className="mu-abrir" onClick={() => abrir(a)}>
                      {aberto ? 'Recolher' : 'Ler aviso completo'}
                    </button>
                  }
                />
              );
            })}
          </div>
        )}
        {avisos.length > 0 && (
          <div className="mu-meu-pe">
            Abrir o aviso conta como lido. Avisos expirados saem desta lista, mas ficam no
            histórico.
          </div>
        )}
      </div>
    </>
  );
}
